import { useState, useRef, useCallback } from 'react'
import { useUnit } from '@/contexts/UnitContext'
import { useAuth } from '@/contexts/AuthContext'
import { useTranslation } from '@/contexts/TranslationContext'
import { useChargeTemplate } from '@/hooks/useChargeTemplate'
import { useDueDateTemplate } from '@/hooks/useDueDateTemplate'
import { usePermissions } from '@/hooks/usePermissions'
import {
  EntityListPage,
  type FilterField,
  type DataTableColumn,
  type CardConfig,
} from '@/components/common'
import { Modal } from '@/components/ui'
import { WhatsAppTemplateModal } from './WhatsAppTemplateModal'
import { QuickChargeModal } from './QuickChargeModal'
import { PartyDocumentModal } from './PartyDocumentModal'
import { WhatsAppHistoryBadge } from './WhatsAppHistoryBadge'
import { WhatsAppHistoryBadgeParty } from './WhatsAppHistoryBadgeParty'
import {
  type PartyWithStats,
  type FinancialEntry,
  type ContactType,
  type Party,
  createParty,
  updateParty,
  deleteOrInactivateParties,
  listPartiesWithStats,
  listEntriesForParty,
  isOverdue,
  logWhatsAppMessage,
  reactivateParty,
  mergeParty,
} from '@/lib/services/billing'
import { openWhatsAppWindow } from '@/lib/whatsapp'
import { matchesSearch } from '@/lib/search'

// ─── Types ────────────────────────────────────────────────────────────────────

interface PartyFormInput {
  name:     string
  whatsapp: string
  pix_key:  string
  notes:    string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(v: number, locale?: string) {
  // Detect locale from document or use provided locale; derive currency from locale
  const currentLocale = locale || document.documentElement.lang || 'pt-BR'
  let currency = 'BRL'
  if (currentLocale.startsWith('en')) {
    currency = 'USD'
  } else if (currentLocale.startsWith('es')) {
    currency = 'EUR'  // or could be other currencies depending on Spanish region
  }
  // pt-BR defaults to BRL
  return v.toLocaleString(currentLocale, { style: 'currency', currency })
}

function formatDate(dateStr: string, locale: string = 'pt-BR') {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(locale)
}

function buildWhatsAppUrl(phone: string, message: string): string {
  const clean  = phone.replace(/\D/g, '')
  const number = clean.startsWith('55') ? clean : `55${clean}`
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}&app_absent=0`
}

const SCORE_STYLES: Record<string, string> = {
  A: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  B: 'bg-green-100   text-green-700   dark:bg-green-900/30   dark:text-green-400',
  C: 'bg-amber-100   text-amber-700   dark:bg-amber-900/30   dark:text-amber-400',
  D: 'bg-orange-100  text-orange-700  dark:bg-orange-900/30  dark:text-orange-400',
  E: 'bg-rose-100    text-rose-700    dark:bg-rose-900/30    dark:text-rose-400',
}

function ScoreChip({ score }: { score: PartyWithStats['score'] }) {
  if (!score) return <span className="text-slate-400 text-xs">—</span>
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${SCORE_STYLES[score]}`}
    >
      {score}
    </span>
  )
}

// ─── Type Chip ────────────────────────────────────────────────────────────────

const TYPE_STYLES: Record<ContactType, string> = {
  customer: 'bg-blue-100   text-blue-700   dark:bg-blue-900/30   dark:text-blue-400',
  supplier: 'bg-slate-100  text-slate-600  dark:bg-slate-700     dark:text-slate-300',
  both:     'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
}

// Icon background styles (used to color the icon wrapper on cards)
const ICON_BG_STYLES: Record<ContactType, string> = {
  customer: 'p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30',
  supplier: 'p-2 rounded-lg bg-slate-100 dark:bg-slate-700',
  both:     'p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30',
}

function TypeChip({
  type,
  labels,
}: {
  type:   ContactType | null
  labels: Record<string, string>
}) {
  if (!type) return <span className="text-slate-400 text-xs">—</span>
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_STYLES[type]}`}>
      {labels[type] ?? type}
    </span>
  )
}

// ─── Financial Profile Modal ──────────────────────────────────────────────────

function FinancialProfileModal({
  party,
  onClose,
  typeLabels,
  scoreLabels,
  profileLabels,
  currentLocale,
}: {
  party:         PartyWithStats | null
  onClose:       () => void
  typeLabels:    Record<string, string>
  scoreLabels:   Record<string, string>
  profileLabels: Record<string, string>
  currentLocale: string
}) {
  if (!party) return null

  const StatCard = ({
    title,
    count,
    total,
    emptyLabel,
    danger,
  }: {
    title:      string
    count:      number
    total:      number
    emptyLabel: string
    danger:     boolean
  }) => (
    <div className={`rounded-lg p-3 border ${
      danger
        ? 'border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-900/20'
        : 'border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-900/20'
    }`}>
      <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${
        danger ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'
      }`}>
        {title}
      </div>
      {count === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{emptyLabel}</p>
      ) : (
        <>
          <div className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">
            {formatCurrency(total, currentLocale)}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {profileLabels.count.replace('{{count}}', String(count))}
          </div>
        </>
      )}
    </div>
  )

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={profileLabels.title.replace('{{name}}', party.name)}
      size="md"
      headerRight={(
        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
            title="Fechar"
            aria-label="Fechar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    >
      <div className="space-y-4">
        {/* Type row */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 dark:text-slate-400 min-w-[80px]">
            {typeLabels.label}:
          </span>
          <TypeChip type={party.party_type} labels={typeLabels} />
        </div>

        {/* Score block */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            {profileLabels.scoreSectionTitle}
          </div>
          {party.score === null ? (
            <p className="text-sm text-slate-400">{profileLabels.noPaidHistory}</p>
          ) : (
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center justify-center h-10 w-10 rounded-full text-xl font-extrabold ${SCORE_STYLES[party.score]}`}>
                {party.score}
              </span>
              <div>
                <div className="text-sm font-semibold text-slate-900 dark:text-white">
                  {scoreLabels[party.score]}
                </div>
                {party.score_pct !== null && (
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {scoreLabels.pct.replace('{{pct}}', String(party.score_pct))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Overdue + Open cards */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            title={profileLabels.overdueSection}
            count={party.overdue_count}
            total={party.overdue_total}
            emptyLabel={profileLabels.noOverdue}
            danger
          />
          <StatCard
            title={profileLabels.openSection}
            count={party.open_count}
            total={party.open_total}
            emptyLabel={profileLabels.noOpen}
            danger={false}
          />
        </div>

        {/* Dates */}
        {(party.first_paid_at || party.last_paid_at) && (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {profileLabels.datesSection}
            </div>
            {party.first_paid_at && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">{profileLabels.firstPaid}</span>
                <span className="font-medium text-slate-900 dark:text-white tabular-nums">
                  {formatDate(party.first_paid_at)}
                </span>
              </div>
            )}
            {party.last_paid_at && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">{profileLabels.lastPaid}</span>
                <span className="font-medium text-slate-900 dark:text-white tabular-nums">
                  {formatDate(party.last_paid_at)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── Merge Party Modal ───────────────────────────────────────────────────────

function MergePartyModal({
  source,
  allParties,
  onConfirm,
  onClose,
}: {
  source:     PartyWithStats
  allParties: PartyWithStats[]
  onConfirm:  (target: Party) => Promise<void>
  onClose:    () => void
}) {
  const [query,      setQuery]      = useState('')
  const [selected,   setSelected]   = useState<PartyWithStats | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  const filtered = allParties
    .filter(p => p.id !== source.id && p.is_active)
    .filter(p => {
      if (!query.trim()) return true
      return matchesSearch(p.name, query)
    })
    .slice(0, 8)

  const handleConfirm = async () => {
    if (!selected) return
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm(selected)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao juntar contatos')
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`Juntar “${source.name}” com...`} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Todas as contas de <strong>{source.name}</strong> serão transferidas para o contato escolhido e o contato antigo será excluído.
        </p>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setSelected(null) }}
            placeholder="Buscar contato..."
            autoFocus
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
          />
        </div>

        {/* Results list */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Nenhum contato encontrado</p>
          ) : filtered.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelected(p)}
              className={`w-full text-left px-3 py-2.5 text-sm transition-colors ${
                selected?.id === p.id
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-800 dark:text-slate-200'
              }`}
            >
              {p.name}
              {p.whatsapp && <span className="ml-2 text-xs text-slate-400">{p.whatsapp}</span>}
            </button>
          ))}
        </div>

        {selected && (
          <div className="text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2 text-amber-700 dark:text-amber-300">
            ⚠️ <strong>{source.name}</strong> será excluído e suas contas movidas para <strong>{selected.name}</strong>. Esta ação não pode ser desfeita.
          </div>
        )}

        {error && <p className="text-sm text-rose-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selected || submitting}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Juntando...' : 'Juntar'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Pendencies Modal ─────────────────────────────────────────────────────────

function PendenciesModal({
  party,
  entries,
  loading,
  onClose,
  labels,
  currentLocale,
}: {
  party:         PartyWithStats | null
  entries:       FinancialEntry[]
  loading:       boolean
  onClose:       () => void
  labels:        { title: string; receivable: string; payable: string; empty: string }
  currentLocale: string
}) {
  if (!party) return null

  const receivable = entries.filter(e => e.entry_type === 'receivable')
  const payable    = entries.filter(e => e.entry_type === 'payable')

  const EntryRow = ({ entry }: { entry: FinancialEntry }) => {
    const overdue = isOverdue(entry)
    return (
      <div className="flex items-start justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900 dark:text-white truncate">
            {entry.description}
          </div>
          <div className={`text-xs ${overdue ? 'text-rose-500' : 'text-slate-500 dark:text-slate-400'}`}>
            {overdue ? '⚠ ' : ''}{formatDate(entry.due_date)}
          </div>
          <div className="mt-1">
            <WhatsAppHistoryBadge entryId={entry.id} />
          </div>
        </div>
        <div className="ml-3 text-right flex-shrink-0">
          <div className="text-sm font-semibold tabular-nums text-slate-900 dark:text-white">
            {formatCurrency(entry.amount, currentLocale)}
          </div>
          <div className={`text-xs ${
            entry.status === 'paid'
              ? 'text-emerald-600 dark:text-emerald-400'
              : overdue
              ? 'text-rose-500'
              : 'text-amber-500'
          }`}>
            {entry.status === 'paid' ? 'Pago' : overdue ? 'Vencida' : 'Em aberto'}
          </div>
        </div>
      </div>
    )
  }

  const Section = ({
    title,
    items,
  }: {
    title:   string
    items:   FinancialEntry[]
  }) => {
    if (items.length === 0) return null
    const total = items.reduce((s, e) => s + e.amount, 0)
    return (
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {title}
          </h4>
          <span className="text-xs font-semibold tabular-nums text-slate-700 dark:text-slate-300">
            {formatCurrency(total, currentLocale)}
          </span>
        </div>
        {items.map(e => <EntryRow key={e.id} entry={e} />)}
      </div>
    )
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={labels.title.replace('{{name}}', party.name)}
      size="md"
      headerRight={(
        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
            title="Fechar"
            aria-label="Fechar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
        </div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
          {labels.empty}
        </p>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto">
          <Section title={labels.receivable} items={receivable} />
          <Section title={labels.payable}    items={payable} />
        </div>
      )}
    </Modal>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ContactsPage() {
  const { t, locale }         = useTranslation()
  const { currentUnit, userRole }       = useUnit()
  const permissions = usePermissions(userRole)
  const { user: authUser }    = useAuth()
  const { template: chargeTemplateStored, saveTemplate: saveChargeTemplate, resetTemplate: resetChargeTemplate, isLoaded: chargeTemplateLoaded } = useChargeTemplate()
  const { template: dueDateTemplateStored, saveTemplate: saveDueDateTemplate, resetTemplate: resetDueDateTemplate, isLoaded: dueDateTemplateLoaded } = useDueDateTemplate()

  // Ref to current contacts data — needed for bulk WhatsApp action
  const contactsRef = useRef<PartyWithStats[]>([])

  // Compact filters state: combobox for contact type and two toggles
  const [typeFilter, setTypeFilter] = useState<'all' | 'receivable' | 'payable' | 'inactive'>('all')
  const [filterOverdue, setFilterOverdue] = useState(false)
  const [filterDueIn7, setFilterDueIn7] = useState(false)

  // Derive locale string from TranslationContext (pt-BR / en-US / es-ES)
  const currentLocale = locale

  // Modals state
  const [profileParty,   setProfileParty]   = useState<PartyWithStats | null>(null)
  const [pendingParty,   setPendingParty]   = useState<PartyWithStats | null>(null)
  const [pendingEntries, setPendingEntries] = useState<FinancialEntry[]>([])
  const [loadingEntries, setLoadingEntries] = useState(false)
  const [chargeTemplate, setChargeTemplate] = useState<PartyWithStats | null>(null)
  const [quickChargeParty, setQuickChargeParty] = useState<PartyWithStats | null>(null)
  const [editingTemplate, setEditingTemplate] = useState(false)
  const [dueDateTemplate, setDueDateTemplate] = useState<PartyWithStats | null>(null)
  const [quickDueDateParty, setQuickDueDateParty] = useState<PartyWithStats | null>(null)
  const [editingDueDateTemplate, setEditingDueDateTemplate] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [mergeSource, setMergeSource] = useState<PartyWithStats | null>(null)
  const [documentParty, setDocumentParty] = useState<PartyWithStats | null>(null)

  const tc = (key: string, params?: Record<string, string>) =>
    t(`billing.contacts.${key}`, params)

  const unitId   = currentUnit?.id ?? ''
  const unitName = currentUnit?.name ?? ''

  // ── Label objects ───────────────────────────────────────────────────────────

  const typeLabels: Record<string, string> = {
    label:    'Tipo',
    customer: tc('contactType.customer'),
    supplier: tc('contactType.supplier'),
    both:     tc('contactType.both'),
    none:     tc('contactType.none'),
  }

  const scoreLabels: Record<string, string> = {
    A:   tc('score.A'),
    B:   tc('score.B'),
    C:   tc('score.C'),
    D:   tc('score.D'),
    E:   tc('score.E'),
    pct: tc('score.pct', { pct: '{{pct}}' }),
  }

  const profileLabels: Record<string, string> = {
    title:             tc('financialProfile.title', { name: '{{name}}' }),
    scoreSectionTitle: tc('financialProfile.scoreSectionTitle'),
    noPaidHistory:     tc('financialProfile.noPaidHistory'),
    overdueSection:    tc('financialProfile.overdueSection'),
    openSection:       tc('financialProfile.openSection'),
    datesSection:      tc('financialProfile.datesSection'),
    firstPaid:         tc('financialProfile.firstPaid'),
    lastPaid:          tc('financialProfile.lastPaid'),
    count:             tc('financialProfile.count', { count: '{{count}}' }),
    total:             tc('financialProfile.total', { amount: '{{amount}}' }),
    noOverdue:         tc('financialProfile.noOverdue'),
    noOpen:            tc('financialProfile.noOpen'),
  }

  const handleOpenPendencies = useCallback(async (party: PartyWithStats) => {
    setPendingParty(party)
    setPendingEntries([])
    setLoadingEntries(true)
    try {
      const entries = await listEntriesForParty(unitId, party.id)
      setPendingEntries(entries)
    } catch {
      // silently ignore
    } finally {
      setLoadingEntries(false)
    }
  }, [unitId])

  // ── WhatsApp helpers ────────────────────────────────────────────────────────

  const openChargeWhatsApp = useCallback((party: PartyWithStats) => {
    if (!party.whatsapp) {
      alert(tc('messages.noWhatsApp'))
      return
    }
    if (party.overdue_count === 0) {
      alert(tc('messages.noOverdue'))
      return
    }
    setQuickChargeParty(party)
  }, [tc])

  const openDueDateWhatsApp = useCallback((party: PartyWithStats) => {
    if (!party.whatsapp) {
      alert(tc('messages.noWhatsApp'))
      return
    }
    if (party.open_count === 0) return
    setQuickDueDateParty(party)
  }, [tc])

  // Helper: render charge template with variables replaced
  const renderMessage = (party: PartyWithStats): string => {
    return chargeTemplateStored
      .replace(/\{\{name\}\}/g, party.name)
      .replace(/\{\{count\}\}/g, String(party.overdue_count))
      .replace(/\{\{amount\}\}/g, formatCurrency(party.overdue_total, currentLocale))
      .replace(/\{\{unit\}\}/g, '')
  }

  // Helper: render duedate template with variables replaced
  const renderDueDateMessage = (party: PartyWithStats): string => {
    return dueDateTemplateStored
      .replace(/\{\{name\}\}/g, party.name)
      .replace(/\{\{count\}\}/g, String(party.due_in_7_days_count))
      .replace(/\{\{amount\}\}/g, formatCurrency(party.due_in_7_days_total, currentLocale))
      .replace(/\{\{unit\}\}/g, '')
  }

  // Reactivate a single party and refresh the list
  const handleReactivate = async (id: string) => {
    try {
      await reactivateParty(id)
      // Force EntityListPage to reload by bumping key (remounts the list component)
      setReloadKey(k => k + 1)
    } catch (err) {
      console.error('[ContactsPage] reactivate error:', err)
      alert(tc('messages.error') || 'Erro ao reativar contato')
    }
  }

  // ── EntityListPage config ───────────────────────────────────────────────────

  const config = {
    entityType: `contacts-${unitId}`,
    filterFields: [] as FilterField[],
    
    // Only allow selecting active parties, but allow selecting inactives when viewing the
    // 'inactive' preset so users can select & reactivate them if needed.
    isEntitySelectable: (party: PartyWithStats) => party.is_active || typeFilter === 'inactive',

    loadData: async () => {
      // Load both active and inactive so the UI can filter locally (shows inactives when requested)
      const data = await listPartiesWithStats(unitId, true)
      contactsRef.current = data
      return data
    },

    createEntity: async (input: PartyFormInput) => {
      const created = await createParty(unitId, input, authUser!.id)
      // Reload parties with stats and return the created one as PartyWithStats
      const parties = await listPartiesWithStats(unitId)
      const found = parties.find(p => p.id === created.id)
      if (!found) return { ...created, ...{
        score_pct: null,
        first_paid_at: null,
        last_paid_at: null,
        overdue_count: 0,
        overdue_total: 0,
        overdue_receivable_count: 0,
        overdue_receivable_total: 0,
        overdue_payable_count: 0,
        overdue_payable_total: 0,
        receivable_count: 0,
        receivable_total: 0,
        payable_count: 0,
        payable_total: 0,
        due_in_7_days_count: 0,
        due_in_7_days_total: 0,
        due_in_7_days_receivable_count: 0,
        due_in_7_days_receivable_total: 0,
        due_in_7_days_payable_count: 0,
        due_in_7_days_payable_total: 0,
        open_count: 0,
        open_total: 0,
      } } as PartyWithStats
      return found
    },

    updateEntity: async (id: string, input: PartyFormInput) => {
      const updated = await updateParty(id, input)
      const parties = await listPartiesWithStats(unitId)
      const found = parties.find(p => p.id === updated.id)
      if (!found) return { ...updated, ...{
        score_pct: null,
        first_paid_at: null,
        last_paid_at: null,
        overdue_count: 0,
        overdue_total: 0,
        overdue_receivable_count: 0,
        overdue_receivable_total: 0,
        overdue_payable_count: 0,
        overdue_payable_total: 0,
        receivable_count: 0,
        receivable_total: 0,
        payable_count: 0,
        payable_total: 0,
        due_in_7_days_count: 0,
        due_in_7_days_total: 0,
        due_in_7_days_receivable_count: 0,
        due_in_7_days_receivable_total: 0,
        due_in_7_days_payable_count: 0,
        due_in_7_days_payable_total: 0,
        open_count: 0,
        open_total: 0,
      } } as PartyWithStats
      return found
    },

    deleteEntity: async (id: string) => {
      // Use smart delete: delete when no open entries, otherwise inactivate
      // No modal feedback needed - EntityListPage handles reload automatically
      await deleteOrInactivateParties(unitId, [id])
      // `useEntityListPage` will call its own reload after deleteEntity resolves
    },

    applyFilters: (parties: PartyWithStats[], query: string, filterValues?: Record<string, any>) => {
      let results = parties

      // Search query (accent- and case-insensitive)
      if (query && query.trim()) {
        results = results.filter(p =>
          matchesSearch(p.name, query) ||
          matchesSearch(p.whatsapp ?? '', query) ||
          matchesSearch(p.pix_key ?? '', query) ||
          matchesSearch(p.notes ?? '', query)
        )
      }

      // Resolve entry type: prefer local combobox, fall back to filterValues
      const entryType = typeFilter
      if (entryType === 'inactive') {
        results = results.filter(p => !p.is_active)
      } else if (entryType === 'receivable') {
        results = results.filter(p => p.is_active && (p.receivable_total ?? 0) > 0)
      } else if (entryType === 'payable') {
        results = results.filter(p => p.is_active && (p.payable_total ?? 0) > 0)
      } else {
        results = results.filter(p => p.is_active)
      }

      // Overdue filter: prefer local toggle, fall back to filterValues
      const overdueFlag = filterOverdue || (filterValues?.overdue as boolean | undefined)
      if (overdueFlag) {
        if (entryType === 'receivable') {
          results = results.filter(p => (p.overdue_receivable_total ?? 0) > 0)
        } else if (entryType === 'payable') {
          results = results.filter(p => (p.overdue_payable_total ?? 0) > 0)
        } else {
          results = results.filter(p => (p.overdue_total ?? 0) > 0)
        }
      }

      // Due in next 7 days filter: prefer local toggle, fall back to filterValues
      const dueIn7Flag = filterDueIn7 || (filterValues?.dueIn7Days as boolean | undefined)
      if (dueIn7Flag) {
        if (entryType === 'receivable') {
          results = results.filter(p => (p.due_in_7_days_receivable_total ?? 0) > 0)
        } else if (entryType === 'payable') {
          results = results.filter(p => (p.due_in_7_days_payable_total ?? 0) > 0)
        } else {
          results = results.filter(p => (p.due_in_7_days_total ?? 0) > 0)
        }
      }

      return results
    },

    validateForm: (data: PartyFormInput) => {
      const errors: Record<string, string> = {}
      if (!data.name.trim()) errors.name = tc('messages.nameRequired')
      return errors
    },
  }

  // ── Table columns ───────────────────────────────────────────────────────────

  const tableColumns: DataTableColumn<PartyWithStats>[] = [
    {
      key:   'name',
      label: tc('list.columns.name'),
      render: (p) => (
        <div className="font-medium text-slate-900 dark:text-white">{p.name}</div>
      ),
    },
    {
      key:   'whatsapp',
      label: tc('list.columns.whatsapp'),
      render: (p) =>
        p.whatsapp ? (
          <a
            href={buildWhatsAppUrl(p.whatsapp!, 'oi')}
            className="text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 flex items-center gap-1"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); openWhatsAppWindow(buildWhatsAppUrl(p.whatsapp!, 'oi')) }}
          >
            <svg className="h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.975-1.395A9.959 9.959 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.948 7.948 0 01-4.048-1.107l-.29-.172-2.953.828.815-2.876-.188-.296A8 8 0 1112 20z" />
            </svg>
            {p.whatsapp}
          </a>
        ) : (
          <span className="text-slate-400 text-sm">—</span>
        ),
    },
    {
      key:   'party_type',
      label: tc('list.columns.type'),
      render: (p) => <TypeChip type={p.party_type} labels={typeLabels} />,
    },
    {
      key:   'score',
      label: tc('list.columns.score'),
      render: (p) => <ScoreChip score={p.score} />,
    },
    {
      key:   'due_in_7_days',
      label: tc('card.dueIn7DaysLabel'),
      render: (p) =>
        p.due_in_7_days_count > 0 ? (
          <div>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {p.due_in_7_days_count}×
            </span>
            <div className="text-xs text-amber-500 tabular-nums mt-0.5">
              {formatCurrency(p.due_in_7_days_total, currentLocale)}
            </div>
          </div>
        ) : (
          <span className="text-slate-400 text-xs">—</span>
        ),
    },
    {
      key:   'overdue',
      label: tc('list.columns.overdue'),
      render: (p) =>
        p.overdue_count > 0 ? (
          <div>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
              {p.overdue_count}×
            </span>
            <div className="text-xs text-rose-500 tabular-nums mt-0.5">
              {formatCurrency(p.overdue_total, currentLocale)}
            </div>
          </div>
        ) : (
          <span className="text-slate-400 text-xs">—</span>
        ),
    },
  ]

  // ── Card config ─────────────────────────────────────────────────────────────

  const cardConfig: CardConfig<PartyWithStats> = {
    getIcon: (p) => (
      <svg className="h-5 w-5 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    getIconWrapperClass: (p) => ICON_BG_STYLES[p.party_type ?? 'customer'],
    getTitle:    (p) => p.name,
      getSubtitle: (p) => p.whatsapp ? (
      <a
        href={buildWhatsAppUrl(p.whatsapp!, 'oi')}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); openWhatsAppWindow(buildWhatsAppUrl(p.whatsapp!, 'oi')) }}
        className="text-xs text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 flex items-center gap-1 font-mono"
      >
        <svg className="h-3.5 w-3.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
          <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.975-1.395A9.959 9.959 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.948 7.948 0 01-4.048-1.107l-.29-.172-2.953.828.815-2.876-.188-.296A8 8 0 1112 20z" />
        </svg>
        {p.whatsapp}
      </a>
    ) : undefined as any,
    getBadge: (p) => {
      if (!p.score) return null
      const variants: Record<string, 'success' | 'warning' | 'danger'> = {
        A: 'success', B: 'success', C: 'warning', D: 'warning', E: 'danger',
      }
      const scoreLabels: Record<string, string> = {
        A: `A — ${tc('score.A')}`,
        B: `B — ${tc('score.B')}`,
        C: `C — ${tc('score.C')}`,
        D: `D — ${tc('score.D')}`,
        E: `E — ${tc('score.E')}`,
      }
      return { label: scoreLabels[p.score], variant: variants[p.score] }
    },
    fields: [
      {
        key:      'party_type',
        label:    'Tipo',
        getValue: (p) => <TypeChip type={p.party_type} labels={typeLabels} />,
      },
      // WhatsApp is shown as subtitle; keep card fields cleaner by not duplicating it here
      {
        key:      'due_in_7_days',
        label:    tc('card.dueIn7DaysLabel'),
        getValue: (p) => p.due_in_7_days_count > 0
          ? `${p.due_in_7_days_count}× ${formatCurrency(p.due_in_7_days_total, currentLocale)}`
          : '-',
      },
      {
        key:      'overdue',
        label:    tc('card.overdueLabel'),
        getValue: (p) => p.overdue_count > 0
          ? (
            <span className="text-rose-600 dark:text-rose-400 font-semibold">
              {`${p.overdue_count}× ${formatCurrency(p.overdue_total, currentLocale)}`}
            </span>
          ) : '-',
      },
      {
        key:      'whatsapp_history',
        label:    'Notificações',
        getValue: (p) => <WhatsAppHistoryBadgeParty partyId={p.id} unitId={unitId} />,
      },
    ],
    editLabel:   tc('actions.edit'),
    deleteLabel: tc('actions.delete'),
    getExtraActions: (p) => {
      const actions: any[] = []
      // Reactivate only for inactive contacts
      if (!p.is_active) {
        actions.push({
          label: tc('actions.activate'),
          icon: (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          onClick: () => handleReactivate(p.id),
          variant: 'success' as const,
        })
        return actions
      }
      // Active contact actions below
      // Charge WhatsApp only if whatsapp exists and has overdue items
      if (p.whatsapp && p.overdue_count > 0) {
        actions.push({
          label:   tc('actions.chargeWhatsApp'),
          icon: (
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.975-1.395A9.959 9.959 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.948 7.948 0 01-4.048-1.107l-.29-.172-2.953.828.815-2.876-.188-.296A8 8 0 1112 20z" />
            </svg>
          ),
          onClick: () => openChargeWhatsApp(p),
          variant: 'success' as const,
        })
      }
      // DueDate alert only if whatsapp exists and has items due in 7 days
      if (p.whatsapp && p.due_in_7_days_count > 0) {
        actions.push({
          label:   tc('actions.dueDateAlert'),
          icon: (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          ),
          onClick: () => openDueDateWhatsApp(p),
          variant: 'warning' as const,
        })
      }
      actions.push({
        label:   tc('actions.financialProfile'),
        icon: (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
          onClick: () => setProfileParty(p),
          variant: 'info' as const,
        })
      actions.push({
        label:   tc('actions.pendencies'),
        icon: (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          ),
          onClick: () => handleOpenPendencies(p),
          variant: 'info' as const,
        })
      
      // Merge and document actions require edit permissions
      if (permissions.canEditEntries) {
        actions.push({
          label: 'Juntar com...',
          icon: (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          ),
          onClick: () => setMergeSource(p),
        })
        actions.push({
          label: tc('actions.documents'),
          icon: (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          onClick: () => setDocumentParty(p),
          variant: 'info' as const,
        })
      }
      return actions
    },
  }

  // ── Bulk WhatsApp ────────────────────────────────────────────────────────────

  const handleBulkWhatsApp = async (ids: string[]) => {
    const selected = contactsRef.current.filter(c => ids.includes(c.id))
    const targets  = selected.filter(c => c.whatsapp && c.overdue_count > 0)
    if (targets.length === 0) {
      alert(tc('messages.bulkNoWhatsApp'))
      return
    }
    for (const p of targets) {
      const msg = tc('whatsapp.chargeMessage', {
        name:   p.name,
        count:  String(p.overdue_count),
        amount: formatCurrency(p.overdue_total, currentLocale),
        unit:   unitName,
      })
      openWhatsAppWindow(buildWhatsAppUrl(p.whatsapp!, msg))
      // Small delay so browser doesn't block multiple popups
      await new Promise(r => setTimeout(r, 400))
    }
  }

  // ── Form renderer ────────────────────────────────────────────────────────────

  const renderForm = (
    formData: PartyFormInput,
    setFormData: (d: PartyFormInput) => void,
    formErrors: Record<string, string>,
    submitting: boolean,
  ) => (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {tc('record.fields.name')} *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={tc('record.fields.namePlaceholder')}
          disabled={submitting}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800
                     px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400
                     focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent
                     disabled:opacity-50"
        />
        {formErrors.name && (
          <p className="mt-1 text-sm text-rose-600">{formErrors.name}</p>
        )}
      </div>

      {/* WhatsApp */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {tc('record.fields.whatsapp')}
        </label>
        <input
          type="tel"
          value={formData.whatsapp}
          onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
          placeholder={tc('record.fields.whatsappPlaceholder')}
          disabled={submitting}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800
                     px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400
                     focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent
                     disabled:opacity-50"
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {tc('record.fields.whatsappHelp')}
        </p>
      </div>

      {/* Pix Key */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {tc('record.fields.pixKey')}
        </label>
        <input
          type="text"
          value={formData.pix_key}
          onChange={(e) => setFormData({ ...formData, pix_key: e.target.value })}
          placeholder={tc('record.fields.pixKeyPlaceholder')}
          disabled={submitting}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800
                     px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 font-mono
                     focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent
                     disabled:opacity-50"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {tc('record.fields.notes')}
        </label>
        <textarea
          rows={3}
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder={tc('record.fields.notesPlaceholder')}
          disabled={submitting}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800
                     px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400
                     focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent
                     disabled:opacity-50 resize-none"
        />
      </div>
    </div>
  )

  // ── Labels ───────────────────────────────────────────────────────────────────

  const labels = {
    title:           tc('list.title'),
    description:     tc('list.description'),
    newButton:       tc('actions.new'),
    searchPlaceholder: tc('filters.searchPlaceholder'),
    emptyTitle:      tc('list.empty.title'),
    emptyDescription: tc('list.empty.description'),
    modalTitleNew:   tc('record.new'),
    modalTitleEdit:  tc('record.edit'),
    modalTitleView:  tc('record.view'),
    deleteTitle:     tc('record.delete'),
    deleteMessage:   tc('record.deleteConfirm'),
    deleteButton:    tc('actions.delete'),
    cancelButton:    tc('actions.cancel'),
    submitButton:    tc('actions.create'),
    updateButton:    tc('actions.update'),
    viewCard:        tc('filters.viewCard'),
    viewTable:       tc('filters.viewTable'),
    clearSearch:     tc('filters.clear'),
  }

  const pageIcon = (
    <svg className="h-6 w-6 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  )

  // Preset chips / badges (fixed filters)
  // Compact chipsLeading: combobox for contact type, plus two toggles
  const chipsLeading = (
    <div className="flex items-center gap-2">
      {/* Contact type combobox */}
      <select
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value as any)}
        className="text-sm rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1 min-w-[180px] text-slate-900 dark:text-white"
      >
        <option value="all">{tc('filters.presets.all')}</option>
        <option value="receivable">{tc('filters.presets.contactsReceivable')}</option>
        <option value="payable">{tc('filters.presets.contactsPayable')}</option>
        <option value="inactive">{tc('filters.presets.contactsInactive')}</option>
      </select>

      <button
        onClick={() => setFilterOverdue(v => !v)}
        className={`shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterOverdue ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500'}`}>
        {tc('filters.presets.overdue')}
      </button>

      <button
        onClick={() => setFilterDueIn7(v => !v)}
        className={`shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterDueIn7 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500'}`}>
        {tc('filters.presets.dueIn7Days')}
      </button>
    </div>
  )

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <EntityListPage<PartyWithStats, PartyFormInput>
        key={reloadKey}
        config={config}
        cardCustomizationStorageKey={`contacts-${unitId}-cards-v2`}
        userRole={userRole}
        canDelete={(p) => !!p.is_active}
        labels={{
          ...labels,
          bulkDeleteButton: typeFilter === 'inactive' ? tc('list.inactiveSelected') : tc('list.deleteSelected'),
        }}
        icon={pageIcon}
        filterFields={[]}
        tableColumns={tableColumns}
        cardConfig={cardConfig}
        renderForm={renderForm}
        entityToFormData={(p) => ({
          name:     p.name,
          whatsapp: p.whatsapp  ?? '',
          pix_key:  p.pix_key   ?? '',
          notes:    p.notes     ?? '',
        })}
        chipsLeading={chipsLeading}
        emptyFormData={() => ({ name: '', whatsapp: '', pix_key: '', notes: '' })}
        enableBulkSelection
        hideBulkDelete={typeFilter === 'inactive'}
        onBulkCustomAction={typeFilter === 'inactive' ? {
          label: tc('actions.activate'),
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          handler: async (ids: string[]) => {
            for (const id of ids) {
              await reactivateParty(id)
            }
            setReloadKey(k => k + 1)
          },
        } : undefined}
        extraRowActions={(p: PartyWithStats) => {
          const actions: any[] = []
          // Show reactivate option or delete based on active state
          if (!p.is_active) {
            actions.push({
              label: tc('actions.activate'),
              icon: (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ),
              onClick: () => handleReactivate(p.id),
            })
          }
          // Merge option for active contacts (requires edit permission)
          if (p.is_active && permissions.canEditEntries) {
            actions.push({
              label: 'Juntar com...',
              icon: (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              ),
              onClick: () => setMergeSource(p),
            })
          }
          // Charge WhatsApp only if whatsapp exists and has overdue items
          if (p.whatsapp && p.overdue_count > 0) {
            actions.push({
              label: tc('actions.chargeWhatsApp'),
              icon: (
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                  <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.975-1.395A9.959 9.959 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.948 7.948 0 01-4.048-1.107l-.29-.172-2.953.828.815-2.876-.188-.296A8 8 0 1112 20z" />
                </svg>
              ),
              onClick: () => openChargeWhatsApp(p),
            })
          }
          // DueDate alert only if whatsapp exists and has items due in 7 days
          if (p.whatsapp && p.due_in_7_days_count > 0) {
            actions.push({
              label: tc('actions.dueDateAlert'),
              icon: (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              ),
              onClick: () => openDueDateWhatsApp(p),
            })
          }
          actions.push({
            label: tc('actions.financialProfile'),
            icon: (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            ),
            onClick: () => setProfileParty(p),
          })
          actions.push({
            label: tc('actions.pendencies'),
            icon: (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            ),
            onClick: () => handleOpenPendencies(p),
          })
          return actions
        }}
        rowQuickAction={(p: PartyWithStats) => (
          p.whatsapp && p.overdue_count > 0 ? {
            icon: (
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.975-1.395A9.959 9.959 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.948 7.948 0 01-4.048-1.107l-.29-.172-2.953.828.815-2.876-.188-.296A8 8 0 1112 20z" />
              </svg>
            ),
            label: tc('actions.chargeWhatsApp'),
            onClick: () => openChargeWhatsApp(p)
          } : null
        )}
        namespace="billing.contacts"
      />

      <FinancialProfileModal
        party={profileParty}
        onClose={() => setProfileParty(null)}
        typeLabels={typeLabels}
        scoreLabels={scoreLabels}
        profileLabels={profileLabels}
        currentLocale={currentLocale}
      />

      {mergeSource && (
        <MergePartyModal
          source={mergeSource}
          allParties={contactsRef.current}
          onConfirm={async (target) => {
            await mergeParty(mergeSource.id, target, userRole || undefined)
            setMergeSource(null)
            setReloadKey(k => k + 1)
          }}
          onClose={() => setMergeSource(null)}
        />
      )}

      <PendenciesModal
        party={pendingParty}
        entries={pendingEntries}
        loading={loadingEntries}
        onClose={() => setPendingParty(null)}
        labels={{
          title:       tc('pendencies.title'),
          receivable:  tc('pendencies.receivable'),
          payable:     tc('pendencies.payable'),
          empty:       tc('pendencies.empty'),
        }}
        currentLocale={currentLocale}
      />

      <WhatsAppTemplateModal
        isOpen={editingTemplate && !!chargeTemplate}
        onClose={() => {
          setEditingTemplate(false)
          setChargeTemplate(null)
        }}
        initialTemplate={chargeTemplateStored}
        availableFields={[
          {
            key: 'name',
            label: tc('templateModal.fields.name'),
            type: 'text',
            value: chargeTemplate?.name ?? '',
          },
          {
            key: 'count',
            label: tc('templateModal.fields.count_overdue'),
            type: 'number',
            value: String(chargeTemplate?.overdue_count ?? 0),
          },
          {
            key: 'amount',
            label: tc('templateModal.fields.amount'),
            type: 'number',
            value: chargeTemplate?.overdue_total ? formatCurrency(chargeTemplate.overdue_total, currentLocale) : '',
          },
          // 'unit' field removed to improve mobile layout (uses default template without unit)
        ]}
        onSave={(finalMessage) => {
          if (chargeTemplate?.whatsapp) {
            // Persist raw template
            saveChargeTemplate(finalMessage)

            // Replace variables for the current party before sending
            const rendered = finalMessage
              .replace(/\{\{name\}\}/g, chargeTemplate.name)
              .replace(/\{\{count\}\}/g, String(chargeTemplate.overdue_count))
              .replace(/\{\{amount\}\}/g, formatCurrency(chargeTemplate.overdue_total, currentLocale))
              .replace(/\{\{unit\}\}/g, '')

            openWhatsAppWindow(buildWhatsAppUrl(chargeTemplate.whatsapp, rendered))
            
            // Log message for each overdue entry (background task, don't wait)
            ;(async () => {
              try {
                const entries = await listEntriesForParty(unitId, chargeTemplate.id)
                const overdue = entries.filter(e => isOverdue(e))
                for (const entry of overdue) {
                  await logWhatsAppMessage({
                    party_id: chargeTemplate.id,
                    entry_id: entry.id,
                    unit_id: unitId,
                    type: 'charge',
                    message_content: finalMessage,
                    phone: chargeTemplate.whatsapp!,
                  })
                }
              } catch (err) {
                console.error('Failed to log WhatsApp message:', err)
              }
            })()
            
            setChargeTemplate(null)
            setEditingTemplate(false)
            setQuickChargeParty(null)
          }
        }}
        title={tc('templateModal.titleCharge')}
      />

      <QuickChargeModal
        isOpen={!!quickChargeParty && !editingTemplate && chargeTemplateLoaded}
        onClose={() => {
          setQuickChargeParty(null)
          setChargeTemplate(null)
        }}
        party={
          quickChargeParty
            ? {
                name: quickChargeParty.name,
                whatsapp: quickChargeParty.whatsapp ?? '',
              }
            : null
        }
        message={quickChargeParty ? renderMessage(quickChargeParty) : ''}
        onConfigure={() => {
          setChargeTemplate(quickChargeParty)
          setEditingTemplate(true)
        }}
        onResetTemplate={() => {
          resetChargeTemplate()
        }}
        onSend={() => {
          if (quickChargeParty) {
            const rendered = renderMessage(quickChargeParty)
            openWhatsAppWindow(buildWhatsAppUrl(quickChargeParty.whatsapp!, rendered))
            
            // Log message for each overdue entry (background task, don't wait)
            ;(async () => {
              try {
                const entries = await listEntriesForParty(unitId, quickChargeParty.id)
                const overdue = entries.filter(e => isOverdue(e))
                for (const entry of overdue) {
                  await logWhatsAppMessage({
                    party_id: quickChargeParty.id,
                    entry_id: entry.id,
                    unit_id: unitId,
                    type: 'charge',
                    message_content: chargeTemplateStored,
                    phone: quickChargeParty.whatsapp!,
                  })
                }
              } catch (err) {
                console.error('Failed to log WhatsApp message:', err)
              }
            })()
            
            setQuickChargeParty(null)
            setChargeTemplate(null)
          }
        }}
      />

      {/* DueDate template editor modal (reutiliza WhatsAppTemplateModal) */}
      <WhatsAppTemplateModal
        isOpen={editingDueDateTemplate && !!dueDateTemplate}
        onClose={() => {
          setEditingDueDateTemplate(false)
          setDueDateTemplate(null)
        }}
        initialTemplate={dueDateTemplateStored}
        availableFields={[
          {
            key: 'name',
            label: tc('templateModal.fields.name'),
            type: 'text',
            value: dueDateTemplate?.name ?? '',
          },
          {
            key: 'count',
            label: tc('templateModal.fields.count_due_soon'),
            type: 'number',
            value: String(dueDateTemplate?.due_in_7_days_count ?? 0),
          },
          {
            key: 'amount',
            label: tc('templateModal.fields.amount'),
            type: 'number',
            value: dueDateTemplate?.due_in_7_days_total ? formatCurrency(dueDateTemplate.due_in_7_days_total, currentLocale) : '',
          },
        ]}
        onSave={(finalMessage) => {
          if (dueDateTemplate?.whatsapp) {
            // Persist raw template
            saveDueDateTemplate(finalMessage)

            // Replace variables for the current party before sending
            const rendered = finalMessage
              .replace(/\{\{name\}\}/g, dueDateTemplate.name)
              .replace(/\{\{count\}\}/g, String(dueDateTemplate.due_in_7_days_count))
              .replace(/\{\{amount\}\}/g, formatCurrency(dueDateTemplate.due_in_7_days_total, currentLocale))
              .replace(/\{\{unit\}\}/g, '')

            openWhatsAppWindow(buildWhatsAppUrl(dueDateTemplate.whatsapp, rendered))
            
            // Log message for each entry due in 7 days (background task, don't wait)
            ;(async () => {
              try {
                const entries = await listEntriesForParty(unitId, dueDateTemplate.id)
                const dueIn7Days = entries.filter(e => {
                  const due = new Date(e.due_date)
                  const today = new Date()
                  const daysUntilDue = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                  return daysUntilDue >= 0 && daysUntilDue <= 7 && e.status === 'open'
                })
                for (const entry of dueIn7Days) {
                  await logWhatsAppMessage({
                    party_id: dueDateTemplate.id,
                    entry_id: entry.id,
                    unit_id: unitId,
                    type: 'duedate_alert',
                    message_content: finalMessage,
                    phone: dueDateTemplate.whatsapp!,
                  })
                }
              } catch (err) {
                console.error('Failed to log WhatsApp message:', err)
              }
            })()
            
            setDueDateTemplate(null)
            setEditingDueDateTemplate(false)
            setQuickDueDateParty(null)
          }
        }}
        title={tc('templateModal.titleDueDate')}
      />

      {/* DueDate quick preview modal (reutiliza QuickChargeModal) */}
      <QuickChargeModal
        isOpen={!!quickDueDateParty && !editingDueDateTemplate && dueDateTemplateLoaded}
        onClose={() => {
          setQuickDueDateParty(null)
          setDueDateTemplate(null)
        }}
        party={
          quickDueDateParty
            ? {
                name: quickDueDateParty.name,
                whatsapp: quickDueDateParty.whatsapp ?? '',
              }
            : null
        }
        message={quickDueDateParty ? renderDueDateMessage(quickDueDateParty) : ''}
        onConfigure={() => {
          setDueDateTemplate(quickDueDateParty)
          setEditingDueDateTemplate(true)
        }}
        onResetTemplate={() => {
          resetDueDateTemplate()
        }}
        onSend={() => {
          if (quickDueDateParty) {
            const rendered = renderDueDateMessage(quickDueDateParty)
            openWhatsAppWindow(buildWhatsAppUrl(quickDueDateParty.whatsapp!, rendered))
            
            // Log message for each entry due in 7 days (background task, don't wait)
            ;(async () => {
              try {
                const entries = await listEntriesForParty(unitId, quickDueDateParty.id)
                const dueIn7Days = entries.filter(e => {
                  const due = new Date(e.due_date)
                  const today = new Date()
                  const daysUntilDue = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                  return daysUntilDue >= 0 && daysUntilDue <= 7 && e.status === 'open'
                })
                for (const entry of dueIn7Days) {
                  await logWhatsAppMessage({
                    party_id: quickDueDateParty.id,
                    entry_id: entry.id,
                    unit_id: unitId,
                    type: 'duedate_alert',
                    message_content: dueDateTemplateStored,
                    phone: quickDueDateParty.whatsapp!,
                  })
                }
              } catch (err) {
                console.error('Failed to log WhatsApp message:', err)
              }
            })()
            
            setQuickDueDateParty(null)
            setDueDateTemplate(null)
          }
        }}
      />

      {documentParty && (
        <PartyDocumentModal
          party={documentParty}
          userRole={userRole}
          onClose={() => setDocumentParty(null)}
          onUpdated={(updated) => {
            setDocumentParty(updated)
            setReloadKey(k => k + 1)
          }}
        />
      )}
    </>
  )
}
